import { ComponentType, useCallback } from 'react';
import noop from 'lodash/noop';
import { useNavigate } from 'react-router-dom';
import * as authenticationService from '../../data-layer/authentication/service';
import { subscribeToBusinessAsync } from '../../data-layer/business/actions';
import { DEFAULT_COLLECTION_ID } from '../../data-layer/collections/service';
import {
    bookmarkPostAsync,
    removePostsAsync,
    sharePostAsync,
    updatePostReactionAsync,
    updatePostStatusAsync,
} from '../../data-layer/posts/actions';
import { PostStatus } from '../../data-layer/posts/constants';
import * as postsService from '../../data-layer/posts/service';
import { PostModel } from '../../data-layer/posts/types';
import { useDispatch } from '../../data-layer/useDispatch';
import { toPostUI } from '../../mappers/posts/toPostUI';
import { confirmAlert } from '../../ui/feedback/Alert';
import { PostUI } from '../../ui/posts/types';
import { canEditBusiness } from '../../utils/accessService';
import { analyticsService, EventSource, VideoStates } from '../../utils/analyticsService';
import {
    getPageNameByEventSource,
    ymTrackClickout,
    ymTrackPostDeleteClick,
    ymTrackPostEditClick,
    ymTrackPostExpand,
    ymTrackPostShare,
} from '../../utils/metrika';
import { useOpenDialog } from '../ModalRoute';
import { DIALOGS } from '../constants';
import { useCurrentUser } from '../users/useCurrentUser';

type OnShareParams = { mediaId: string };

type OnClickOutClickParams = { mediaId: string };

type OnReactionParams = { postNumber: number; reactionState: PostModel['reactionState'] };

type OnSubscribeParams = { postNumber: number };

type OnSlideChangeParams = {
    mediaId: string;
    mediaIndex: number;
};

type OnExpandParams = {
    mediaId: string;
    mediaIndex: number;
};

type OnVisibilityChangeParams = {
    mediaId: string;
    isInViewport: boolean;
    postNumber: number;
};

type OnMuteChangeParams = {
    mediaId: string;
    mute: boolean;
};

type OnVideoStateChangeParams = {
    mediaId: string;
};

export interface WithPostCallbacksProps {
    onEdit?: VoidFunction;
    onRemove?: VoidFunction;
    onReport?: VoidFunction;
    onDraft?: VoidFunction;
    onPublish?: VoidFunction;
    onBookmark?: VoidFunction;
    onShare?: (params: OnShareParams) => void;
    onExpand?: (params: OnExpandParams) => void;
    onReaction: (params: OnReactionParams) => void;
    onSubscribe?: (params: OnSubscribeParams) => void;
    onSlideChange: (params: OnSlideChangeParams) => void;
    onClickOutClick: (params: OnClickOutClickParams) => void;
    onVisibilityChange: (params: OnVisibilityChangeParams) => void;
    onVideoMuteChange: (params: OnMuteChangeParams) => void;
    onVideoEnd: (params: OnVideoStateChangeParams) => void;
    onVideoStart: (params: OnVideoStateChangeParams) => void;
    onVideoPlay: (params: OnVideoStateChangeParams) => void;
    onVideoPause: (params: OnVideoStateChangeParams) => void;
}

export function withPostCallbacks<T>(
    BaseComponent: ComponentType<T & WithPostCallbacksProps>,
    eventSource: EventSource,
): ComponentType<
    Omit<Pick<T, Exclude<keyof T, keyof WithPostCallbacksProps>>, 'post' | 'onExpand'> & {
        post: PostModel;
        onExpand?: VoidFunction;
        collectionId?: string;
    }
> {
    return (props) => {
        const { post, collectionId, ...other } = props;
        const dispatch = useDispatch();
        const currentUser = useCurrentUser();
        const openDialog = useOpenDialog();
        const navigate = useNavigate();

        const onShare = useCallback(
            (params: OnShareParams) => {
                dispatch(sharePostAsync(post.id));
                analyticsService.trackShare({
                    postId: post.id,
                    djRequestId: post.djRequestId,
                    mediaId: params.mediaId,
                    eventSource,
                });
                ymTrackPostShare();
            },
            [post, dispatch],
        );

        const onReaction = useCallback(
            authenticationService.protect(
                (params: { postNumber: number; reactionState: PostUI['reactionState'] }) =>
                    dispatch(
                        updatePostReactionAsync({
                            postId: post.id,
                            postNumber: params.postNumber,
                            reactionState: params.reactionState,
                            eventSource,
                        }),
                    ),
            ),
            [post, dispatch],
        );

        const onReport = useCallback(() => {
            openDialog(DIALOGS.REPORT_DIALOG, { targetId: post.id, targetType: 'post' });
        }, [post]);

        const onSubscribe = useCallback(
            authenticationService.protect((params: { postNumber: number }) =>
                dispatch(
                    subscribeToBusinessAsync(
                        post.business,
                        eventSource,
                        params.postNumber,
                        post.id,
                    ),
                ),
            ),
            [post, dispatch],
        );

        const onBookmark = useCallback(
            authenticationService.protect(async () => {
                if (post.saved && collectionId && collectionId !== DEFAULT_COLLECTION_ID) {
                    return openDialog(DIALOGS.DELETE_FROM_COLLECTION_DIALOG_NAME, {
                        postId: post.id,
                        collectionId,
                    });
                }

                const isSaved = await dispatch(bookmarkPostAsync(post.id));

                if (isSaved) {
                    openDialog(DIALOGS.SAVE_TO_COLLECTION_DIALOG_NAME, { postId: post.id });
                }
            }),
            [post, dispatch, openDialog, collectionId],
        );

        const onClickOutClick = useCallback(
            (params: OnClickOutClickParams) => {
                analyticsService.trackClickOut({
                    postId: post.id,
                    djRequestId: post.djRequestId,
                    mediaId: params.mediaId,
                    eventSource,
                });
                ymTrackClickout();
            },
            [post],
        );

        const onSlideChange = useCallback(
            (params: OnSlideChangeParams) => {
                analyticsService.trackSlideMoved({
                    postId: post.id,
                    djRequestId: post.djRequestId,
                    mediaId: params.mediaId,
                    eventSource,
                    sliderContentNumber: params.mediaIndex,
                });
            },
            [post],
        );

        const onVideoEnd = useCallback(
            (params: OnVideoStateChangeParams) => {
                analyticsService.trackVideoStateChange({
                    postId: post.id,
                    djRequestId: post.djRequestId,
                    eventSource: eventSource,
                    mediaId: params.mediaId,
                    state: {
                        eventType: VideoStates.VIDEO_FINISHED,
                        wasNotTriggeredByUser: true,
                    },
                });
            },
            [post],
        );

        const onVideoStart = useCallback(
            (params: OnVideoStateChangeParams) => {
                analyticsService.trackVideoStateChange({
                    postId: post.id,
                    djRequestId: post.djRequestId,
                    eventSource: eventSource,
                    mediaId: params.mediaId,
                    state: {
                        eventType: VideoStates.VIDEO_STARTED,
                    },
                });
            },
            [post],
        );

        const onVideoPlay = useCallback(
            (params: OnVideoStateChangeParams) => {
                analyticsService.trackVideoStateChange({
                    postId: post.id,
                    djRequestId: post.djRequestId,
                    eventSource: eventSource,
                    mediaId: params.mediaId,
                    state: {
                        eventType: VideoStates.VIDEO_RESUMED,
                    },
                });
            },
            [post],
        );

        const onVideoPause = useCallback(
            (params: OnVideoStateChangeParams) => {
                analyticsService.trackVideoStateChange({
                    postId: post.id,
                    djRequestId: post.djRequestId,
                    eventSource: eventSource,
                    mediaId: params.mediaId,
                    state: {
                        eventType: VideoStates.VIDEO_PAUSED,
                    },
                });
            },
            [post],
        );

        const onExpand = useCallback(
            (params: OnExpandParams) => {
                other.onExpand?.();
                analyticsService.trackPostExpanded({
                    postId: post.id,
                    djRequestId: post.djRequestId,
                    mediaId: params.mediaId,
                    sliderContentNumber: params.mediaIndex,
                    eventSource,
                });
                ymTrackPostExpand();
            },
            [post],
        );

        const onVisibilityChange = useCallback(
            (params: OnVisibilityChangeParams) => {
                analyticsService.trackPostVisible({
                    ...params,
                    postId: post.id,
                    djRequestId: post.djRequestId,
                    eventSource: eventSource,
                });
            },
            [post],
        );

        const onVideoMuteChange = useCallback(
            (params: OnMuteChangeParams) => {
                analyticsService.trackVideoStateChange({
                    postId: post.id,
                    djRequestId: post.djRequestId,
                    eventSource: eventSource,
                    mediaId: params.mediaId,
                    state: {
                        eventType: params.mute
                            ? VideoStates.VIDEO_MUTED
                            : VideoStates.VIDEO_UN_MUTED,
                    },
                });
            },
            [post],
        );

        const onRemove = useCallback(() => {
            ymTrackPostDeleteClick({
                page: getPageNameByEventSource(eventSource),
                userLogin: currentUser?.login,
                businessId: currentUser?.profile?.publicId,
            });
            confirmAlert('Удаление', 'Вы действительно хотите безвозвратно удалить пост?')
                .then(() => dispatch(removePostsAsync(post.id)))
                .catch(noop);
        }, [post.id, currentUser?.login, currentUser?.profile?.publicId, eventSource, dispatch]);

        const onEdit = useCallback(() => {
            ymTrackPostEditClick({
                page: getPageNameByEventSource(eventSource),
                userLogin: currentUser?.login,
                businessId: currentUser?.profile?.publicId,
            });
            navigate(`/posts/${post.id}/edit`);
        }, [post, currentUser?.login, currentUser?.profile?.publicId, eventSource, navigate]);

        const makeUpdateStatusHandler = useCallback(
            (status: PostStatus) => {
                if (postsService.canChangeStatus(post, status, currentUser)) {
                    return () => dispatch(updatePostStatusAsync([post.id], status, currentUser));
                }

                return undefined;
            },
            [currentUser?.role],
        );

        return (
            // @ts-ignore
            <BaseComponent
                {...other}
                post={toPostUI(post)}
                onEdit={canEditBusiness(post.business.id, currentUser) ? onEdit : undefined}
                onDraft={makeUpdateStatusHandler(PostStatus.DRAFT)}
                onPublish={makeUpdateStatusHandler(PostStatus.MODERATION)}
                onRemove={postsService.canRemove(post, currentUser) ? onRemove : undefined}
                onShare={onShare}
                onReport={onReport}
                onReaction={onReaction}
                onSubscribe={onSubscribe}
                onSlideChange={onSlideChange}
                onBookmark={onBookmark}
                onClickOutClick={onClickOutClick}
                onVisibilityChange={onVisibilityChange}
                onExpand={onExpand}
                onVideoMuteChange={onVideoMuteChange}
                onVideoEnd={onVideoEnd}
                onVideoStart={onVideoStart}
                onVideoPlay={onVideoPlay}
                onVideoPause={onVideoPause}
            />
        );
    };
}
